import { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Send } from 'lucide-react';
import useBottomSheetSwipe from '../../hooks/useBottomSheetSwipe';
import { SHEET_TOKENS, getGlassSurface, getTextColors, getBackdropStyle, getHandleStyle, getInnerCardSurface } from './sheetStyle';
import { SheetTextarea, SheetSelect, SheetButton } from './SheetUI';

export default function TeamMessageSheet({ tym, initialPrijemce, onClose, onSend, isDark, isMobile }) {
    const [typ, setTyp] = useState(initialPrijemce ? 'individual' : 'skupina');
    const [prijemce, setPrijemce] = useState(initialPrijemce?.zamestnanec_id || null);
    const [text, setText] = useState('');
    const [sending, setSending] = useState(false);
    const { dragHandleProps, modalStyle: swipeStyle } = useBottomSheetSwipe(onClose, { enabled: !!isMobile });

    const handleSend = async () => {
        if (!text.trim() || (typ === 'individual' && !prijemce)) return;
        setSending(true);
        try {
            const ok = await onSend({ typ, text, prijemce_zamestnanec_id: prijemce });
            if (ok) { setText(''); onClose?.(); }
        } finally {
            setSending(false);
        }
    };

    const glass = getGlassSurface(isDark);
    const colors = getTextColors(isDark);
    const inner = getInnerCardSurface(isDark);
    const slotBg = inner.background;
    const slotBorder = inner.border.split(' ').pop();
    const labelMuted = colors.labelMuted;
    const textMuted = colors.textMuted;
    const textPrimary = colors.textPrimary;

    const odeslatDisabled = sending || !text.trim() || (typ === 'individual' && !prijemce);

    // Portal do body — uniká stacking-context trapu.
    return createPortal((
        <div onClick={onClose} style={{
            ...getBackdropStyle(),
            zIndex: 1050,
            alignItems: 'center', justifyContent: 'flex-end',
            padding: 0,
        }}>
            <div onClick={e => e.stopPropagation()} className="ms-sheet-anim" style={{
                width: '100%', maxWidth: isMobile ? '100%' : 480,
                minHeight: isMobile ? '50vh' : 'auto',
                maxHeight: isMobile ? '85vh' : '90vh',
                paddingBottom: 'env(safe-area-inset-bottom, 0px)',
                borderRadius: `${SHEET_TOKENS.radius.container}px ${SHEET_TOKENS.radius.container}px 0 0`,
                ...glass,
                display: 'flex', flexDirection: 'column',
                overflow: 'hidden',
                ...swipeStyle,
            }}>
                {isMobile && (
                    <div {...dragHandleProps} style={{
                        display: 'flex', justifyContent: 'center',
                        paddingTop: 10, paddingBottom: 6,
                        cursor: 'grab', flexShrink: 0,
                    }}>
                        <span style={getHandleStyle(isDark)} />
                    </div>
                )}

                <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12, flex: 1, minHeight: 0 }}>
                    {/* Header — oranžová tečka + label vlevo, close vpravo */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 7,
                            color: '#FF8A00',
                            fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase',
                        }}>
                            <span style={{
                                width: 8, height: 8, borderRadius: '50%',
                                background: '#FF8A00', boxShadow: '0 0 8px rgba(255,138,0,0.5)',
                            }} />
                            Zpráva týmu
                        </span>
                        <button type="button" onClick={onClose} style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            color: textMuted, padding: 0,
                        }}>
                            <X size={20} />
                        </button>
                    </div>

                    {/* Toggle — Celá směna / Jednotlivec (slot-style segments) */}
                    <div style={{ display: 'flex', gap: 6 }}>
                        {[{ id: 'skupina', label: 'Celá směna' }, { id: 'individual', label: 'Jednotlivec' }].map(t => {
                            const active = typ === t.id;
                            return (
                                <button key={t.id} type="button" onClick={() => setTyp(t.id)} style={{
                                    flex: 1, padding: '10px', borderRadius: 9,
                                    background: active ? 'rgba(255,138,0,0.12)' : slotBg,
                                    color: active ? '#FF8A00' : textMuted,
                                    border: `1px solid ${active ? 'rgba(255,138,0,0.45)' : slotBorder}`,
                                    fontSize: 12, fontWeight: 700, letterSpacing: 0.2, cursor: 'pointer',
                                }}>{t.label}</button>
                            );
                        })}
                    </div>

                    {typ === 'individual' && (
                        <SheetSelect
                            isDark={isDark}
                            placeholder="— Vyberte příjemce —"
                            value={prijemce || ''}
                            onChange={e => setPrijemce(Number(e.target.value) || null)}
                            options={tym.map(t => ({ value: t.zamestnanec_id, label: `${t.jmeno} ${t.prijmeni}` }))}
                        />
                    )}

                    <SheetTextarea
                        isDark={isDark}
                        value={text}
                        onChange={e => setText(e.target.value.slice(0, 500))}
                        placeholder="Napište zprávu…"
                        rows={5}
                        style={{ flex: 1, minHeight: 100, resize: 'none' }}
                    />

                    <div style={{
                        display: 'flex', justifyContent: 'flex-end',
                        fontSize: 11, color: labelMuted, fontVariantNumeric: 'tabular-nums',
                        marginTop: -6,
                    }}>{text.length}/500</div>

                    <SheetButton
                        isDark={isDark}
                        variant="primary"
                        busy={sending}
                        busyLabel="Odesílám…"
                        disabled={odeslatDisabled}
                        onClick={handleSend}
                        icon={<Send size={16} />}
                    >Odeslat</SheetButton>
                </div>
            </div>
        </div>
    ), document.body);
}
